import type { Twitter } from "next/dist/lib/metadata/types/twitter-types";
import type { ArticleAuthor } from "rivals/shared/interfaces/Author";
import type { CareerType } from "rivals/shared/interfaces/Career";
import type { Prospect } from "rivals/shared/interfaces/Prospect";
import type { CustomModuleSite, Site } from "rivals/shared/interfaces/Site";

export type Programming = [number, string | null];

export enum BlockType {
  Ad = "ad",
  Athlete = "prospectcard",
  Divider = "divider",
  Gallery = "gallery",
  Heading = "heading",
  Image = "image",
  Instagram = "instagram",
  List = "list",
  Quote = "pullquote",
  Tweet = "tweet",
  Template = "template",
  Table = "table",
  Text = "text",
  Video = "video"
}

export enum TemplateName {
  ApplePodcast = "Apple Podcast",
  FacebookVideo = "Facebook Video",
  FloridaGatorsVideo = "Florida Gators Video",
  GoogleSheets = "Google Sheets",
  Instagram = "Instagram",
  Libsyn = "Libsyn Podcast",
  PodBean = "PodBean",
  SendToNews = "SendToNews",
  Soundcloud = "SoundCloud",
  Spotify = "Spotify",
  YahooSportsVideo = "Yahoo Sports Video (Krysten)"
}

export interface AdBlock {
  data: {};
  type: BlockType.Ad;
}

interface AthleteBlock {
  data: {
    athleteSlug: string | null;
    id: number;
    playerType: CareerType;
  };
  type: BlockType.Athlete;
}

export interface DividerBlock {
  data: {};
  type: BlockType.Divider;
}

export interface GalleryBlock {
  data: { id: string };
  type: BlockType.Gallery;
}

export interface HeadingBlock {
  data: { format: "html"; text: string };
  type: BlockType.Heading;
}

interface ImageBlockData extends Image {
  alt: string;
  editUrl: string;
  galleries: [];
  prospects: string;
  prospectsData: Prospect[];
  url: string;
}

export interface ImageBlock {
  data: ImageBlockData;
  type: BlockType.Image;
}

export interface InstagramBlock {
  data: {
    html: string;
    instagramUrl: string;
  };
  type: BlockType.Instagram;
}

export interface ListBlock {
  data: {
    id: number;
    listType: string;
  };
  type: BlockType.List;
}

export interface QuoteBlock {
  data: {
    credit: string;
    text: string;
  };
  type: BlockType.Quote;
}

export interface TableBlock {
  data: {
    /** The caption of the table */
    caption?: string;
    /** Collection of table rows as returned by the contents API */
    content: { type: string; value: string }[][];
    /** Collection of columns to be displayed as returned by the contents API */
    headers: { name: string; type: string }[];
    /** The title of the table */
    title: string;
  };
  type: BlockType.Table;
}

export interface TemplateBlock {
  data: {
    /** Arguments for the template as a JSON string */
    arguments: string;
    /** Identifying name of the template */
    templateName: TemplateName;
  };
  type: BlockType.Template;
}

export interface TextBlock {
  data: {
    format: "html";
    text: string;
  };
  type: BlockType.Text;
}

export interface TweetBlock {
  data: {
    html: string;
    tweetUrl: string;
  };
  type: BlockType.Tweet;
}

export interface VideoBlock {
  data: {
    autoplay?: boolean;
    remoteId: string;
    source: string;
  };
  type: BlockType.Video;
}

export type Block =
  | AdBlock
  | AthleteBlock
  | DividerBlock
  | GalleryBlock
  | HeadingBlock
  | ImageBlock
  | InstagramBlock
  | ListBlock
  | QuoteBlock
  | TableBlock
  | TemplateBlock
  | TextBlock
  | TweetBlock
  | VideoBlock;

interface AdCampaign {
  hashtag: string;
  id: number;
  name: string;
}

export interface Article {
  adCampaign: AdCampaign;
  authorInfo: ArticleAuthor;
  body: Block[];
  canEdit: boolean;
  id: number;
  imageUrl: string;
  isPremium: boolean;
  isPreview: boolean;
  jcacheKey?: string;
  metaDescription: string;
  metaTitle: string;
  modifiedTime: string;
  permalink: string;
  prospectNames: string[];
  publishedTime: string;
  publishingSite: Site;
  requestingSiteID?: number;
  section: ArticleType;
  slug: string;
  sport?: string | null;
  summary: string;
  thumbnail: string;
  title: string;
  topicTags: string;
  twitter: Twitter & {
    card: "summary_large_image";
  };
}

interface AssociatedLink {
  contentId: number | null;
  createdAt: string;
  displayText: string | null;
  id: number;
  ordinality: number;
  settingId: number | null;
  updatedAt: string;
  url: string | null;
}

export type ContentKind = "free" | "premium";

export type ArticleType = "Article" | "Forum Thread" | "Link" | "Video";

/**
 * Minimal interface for a content card item
 */
export interface ContentCardItem {
  authorName: string;
  contentType: string;
  contentUrl: string;
  headlinePrimaryUrl: string;
  heroDesktopUrl: string;
  kind: ContentKind;
  liveAt: string;
  siteId: number;
  sport: string | null;
  summary: string;
  title: string;
}

export interface ContentItem extends ContentCardItem {
  associatedLinks: AssociatedLink[];
  authorId: number;
  authorTitle: string;
  deleted: false;
  emphasizeCardStyle: boolean;
  hasPreview: boolean;
  heroMobileUrl: string;
  heroUrl: string;
  id: number;
  newsType: string;
  permalink: string;
  programming: Programming[];
  siteName: string;
  slug: string;
  thumbnailUrl: string;
  type: string;
}

export interface Gallery {
  adReferer?: string;
  authorName: string;
  createdAt: string;
  currentSiteName?: string;
  description: string;
  draft: boolean;
  galleryId: string;
  id: string;
  images: GalleryImage[];
  imagesCount: number;
  lastModifyingSiteId: number;
  liveAt: string;
  modifyingAuthor?: string;
  modifyingAuthorName: string;
  modifyingSite: string;
  name: string;
  originalAuthor: string;
  originalAuthorId: number;
  originalSite: string;
  originalSiteId: number;
  premium: boolean;
  publishStatus: string;
  siteIdentifier: string;
  spaceId?: number;
  summary: string;
  updatedAt: string;
  upsell?: boolean;
}

interface Image {
  caption: string;
  createdAt: string | null;
  credit: string | null;
  description: string | null;
  galleryPreviewUrl: string;
  imageId: number;
  largeUrl: string;
  publicId: string;
  searchPreviewUrl: string;
  siteName: string | null;
  thumbnailUrl: string;
  uploaderId: number | null;
  uploaderName: string;
}

export interface GalleryImage extends Image {
  fullImageUrl: string;
  galleryImageId: number;
  ordinality: number;
  prospects: Prospect[];
}

/**
 * Unique to the custom module content API
 * Do not use elsewhere
 */
export interface CustomModuleContent {
  associatedLinks: AssociatedLink[];
  authorName: string;
  contentType: string;
  createdAt: string;
  creatorAuthorName: string;
  deleted: boolean;
  draft: boolean;
  emphasizeCardStyle: boolean;
  externalContentUrl: string | null;
  headlinePrimaryUrl: string;
  heroDesktopUrl: string;
  id: number;
  kind: ContentKind;
  live: boolean;
  liveAt: string;
  modifyingAuthorName: string;
  permalink: string;
  premium: boolean;
  publishStatus: string;
  site: CustomModuleSite;
  siteId: number;
  siteName: string;
  slug: string;
  sport: string | null;
  summary: string;
  thumbnailUrl: string;
  title: string;
  updatedAt: string;
  url: string;
}

export interface ContentMeta {
  contentId: number;
  contentSlug: string;
  siteId: number;
}
